// #region: Class definition *********************************************************************/
/**
 * @class DraggableList
 * @description Custom web component that renders a draggable-list component with support for cross-list drag and drop.
 * 
 * @extends WebComponent
 * Inspired by: 
 *      https://github.com/formkit/drag-and-drop
 *      https://codepen.io/tahazsh/pen/KKGJggG
 *  
 * @example
 * <draggable-list [drop-target] [drag-source] [allow-cross-list]>
 *     ...elements (div)
 * </draggable-list>
 *  
 * @property {string} drop-target - List of draggable-list IDs that can drop items onto this list (comma-separated).
 * @property {string} drag-source - List of draggable-list IDs that this list can drag items to (comma-separated).
 * @property {boolean} allow-cross-list - Boolean attribute that enables cross-list drag and drop (shorthand for allowing all lists).
 * 
 * @remarks
 *     Events:
 *     - itemdropped: Dispatched when an item is dropped within the same list.
 *     - itemmovedinto: Dispatched when an item is moved into this list from another list.
 */
class DraggableList extends WebComponent {

    // #region: Private Fields *******************************************************************/
    /**
     * Flag indicating if cross-list drag and drop is allowed.
     * @type {boolean}
     * 
     * @private
     */
    #allowCrossList = false;

    /**
     * Animation frame ID for ghost element positioning.
     * @type {number|null}
     * 
     * @private
     */
    #animationFrameId = null;

    /**
     * Current drag operation state data.
     * @type {Object|null}
     * 
     * @private
     */
    #dragState = null;

    /**
     * Ghost element for visual feedback during drag operations.
     * @type {HTMLElement|null}
     * 
     * @private
     */
    #ghostElement = null;

    /**
     * Map of draggable elements to their node data.
     * @type {Map<HTMLElement, Object>}
     * 
     * @private
     */
    #nodes = new Map();

    /**
     * Reference to the original parent list for restoration.
     * @type {DraggableList|null}
     * 
     * @private
     */
    #originalParentList = null;

    /**
     * Original position data for element restoration.
     * @type {Object|null}
     * 
     * @private
     */
    #originalPosition = null;

    /**
     * Horizontal scroll speed for auto-scrolling.
     * @type {number}
     * 
     * @private
     */
    #scrollSpeedHorizontal = 20;

    /**
     * Vertical scroll speed for auto-scrolling.
     * @type {number}
     * 
     * @private
     */
    #scrollSpeedVertical = 10;

    /**
     * Threshold distance from viewport edge to trigger auto-scrolling.
     * @type {number}
     * 
     * @private
     */
    #scrollThreshold = 50;

    /**
     * Timeout ID for auto-scroll operations.
     * @type {number|null}
     * 
     * @private
     */
    #scrollTimeout = null;

    /**
     * Internal component state object.
     * @type {Object}
     * 
     * @private
     */
    #state = {
        activeDescendant: undefined,
        coordinates: { x: 0, y: 0 },
        currentTargetValue: undefined,
        dragging: false,
        nodes: [],
        preventEnter: false,
        touchDragging: false,
        touchStartX: 0,
        touchStartY: 0
    };

    /**
     * Timeout ID for touch operations.
     * @type {number|null}
     * 
     * @private
     */
    #touchTimeout = null;

    /**
     * Set of valid drag source list IDs.
     * @type {Set<string>}
     * 
     * @private
     */
    #validDragSources = new Set();

    /**
     * Set of valid drop target list IDs.
     * @type {Set<string>}
     * 
     * @private
     */
    #validDropTargets = new Set();
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Creates an instance of DraggableList.
     */
    constructor() {
        super();

        // Event handlers bound to the instance to avoid losing context.
        this.debouncedScroll = this.debouncedScroll.bind(this);
        this.handleDragEnd = this.handleDragEnd.bind(this);
        this.handleDragMove = this.handleDragMove.bind(this);
        this.handleDragStart = this.handleDragStart.bind(this);
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * This is used by the browser to identify and register the custom element.
     * @type {string}
     * @static
     * @memberof DraggableList
     */
    static tagName = 'draggable-list';

    /**
     * Identifies class instance.
     * @returns {string} The string representation of this class instance.
     */
    toString() {
        return '[DraggableList]';
    }

    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     * 
     * @static
     */
    static toString() {
        return '[DraggableList]';
    }

    /** 
     * Identifies the version.
     * @returns {string} The version number of this class.
     * 
     * @static
     */
    static version() {
        return '1.0.0';
    }
    // #endregion ********************************************************************************/

    // #region: Static Properties ****************************************************************/
    /**
     * Defines the observed attributes for the custom element.
     * @returns {string[]} Array of attribute names to observe.
     * 
     * @static
     */
    static get observedAttributes() {
        return [...(super.observedAttributes || []), 'drop-target', 'drag-source', 'allow-cross-list'];
    }
    // #endregion ********************************************************************************/

    // #region: Properties ***********************************************************************/
    /**
     * Gets the list of valid drop targets.
     * @returns {Set<string>} Set of list IDs that can drop items onto this list.
     */
    get validDropTargets() {
        return this.#validDropTargets;
    }

    /**
     * Gets the list of valid drag sources.
     * @returns {Set<string>} Set of list IDs that this list can drag items to.
     */
    get validDragSources() {
        return this.#validDragSources;
    }

    /**
     * Checks if cross-list drag and drop is allowed.
     * @returns {boolean} True if cross-list drag and drop is allowed.
     */
    get allowCrossList() {
        return this.#allowCrossList;
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Called when the custom element is attached to the DOM.
     * Sets up drag and drop functionality.
     */
    connectedCallback() {
        super.connectedCallback();

        // Initialize cross-list settings.
        this.#initializeCrossListSettings();

        this.#addEventListeners();
        this.#initializeNodes();
    }

    /**
     * Called when observed attributes change.
     * @param {string} name The name of the attribute that changed.
     * @param {string|null} oldValue The previous value of the attribute.
     * @param {string|null} newValue The new value of the attribute.
     */
     
    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'drop-target' || name === 'drag-source' || name === 'allow-cross-list') {
            this.#initializeCrossListSettings();
        }
    }

    /**
     * Called when the custom element is removed from the DOM.
     * Cleans up event listeners and resets state.
     */
    disconnectedCallback() {
        super.disconnectedCallback();

        this.#removeEventListeners();
        this.#reset();
    }
    // #endregion ********************************************************************************/

    // #region: Event Handlers *******************************************************************/
    /**
     * Debounced version of scroll.
     * @param {number} clientX The current X position.
     * @param {number} clientY The current Y position.
     * 
     * @memberof DraggableList
     */
    debouncedScroll = (clientX, clientY) => {
        if (this.#scrollTimeout) {
            window.cancelAnimationFrame(this.#scrollTimeout);
        }

        this.#scrollTimeout = window.requestAnimationFrame(() => {
            this.#autoScroll(clientX, clientY);
        });
    };

    /**
     * Handles drag end event (mouse up, touch end, or touch cancel).
     * @param {MouseEvent|TouchEvent} e The drag end event.
     * 
     * @memberof DraggableList
     */
    handleDragEnd(e) {
        if (!this.#dragState) {
            return;
        }

        const isTouch = e.type.startsWith('touch');

        // Remove event listeners.
        if (isTouch) {
            document.removeEventListener('touchmove', this.handleDragMove);
            document.removeEventListener('touchend', this.handleDragEnd);
            document.removeEventListener('touchcancel', this.handleDragEnd);
        }
        else {
            document.removeEventListener('mousemove', this.handleDragMove);
            document.removeEventListener('mouseup', this.handleDragEnd);
        }

        // Clean up.
        if (this.#dragState.draggedNode && this.#dragState.draggedNode.element) {
            const draggableElement = this.#dragState.draggedNode.element;
            draggableElement.classList.remove('dragging');

            // Get the current parent list and check if it changed.
            const currentParentList = draggableElement.closest('draggable-list');

            // If the element is not in any draggable-list, return it to the original position.
            if (!currentParentList) {
                this.#restoreOriginalPosition(draggableElement);

                // Update original list's indices.
                this.#dragState.parentList.#updateNodeIndices();
            }
            else if (draggableElement.id) {
                if (currentParentList === this.#dragState.parentList) {
                    // Item dropped within the same list.
                    // Calculate the new index from the DOM position
                    const newIndex = Array.from(currentParentList.querySelectorAll(':scope > .draggable')).indexOf(draggableElement);
                    this.#dispatchDropEvent(draggableElement.id, newIndex);
                }
                else {
                    // Dispatch an event to the target list.
                    currentParentList.dispatchEvent(new CustomEvent('itemmovedinto', {
                        detail: {
                            fromListId: this.#dragState.parentList.id,
                            id: draggableElement.id,
                            newIndex: Array.from(currentParentList.querySelectorAll(':scope > .draggable')).indexOf(draggableElement)
                        }
                    }));
                }
            }
        }

        // Remove "can-drop" class from all lists.
        document.querySelectorAll('draggable-list.can-drop').forEach((list) => {
            list.classList.remove('can-drop');
        });

        // Update all lists involved.
        this.#updateNodeIndices();

        // If there's a different parent list now, update its nodes too.
        if (this.#dragState.currentHoverList && this.#dragState.currentHoverList !== this.#dragState.parentList) {
            this.#dragState.currentHoverList.#updateNodeIndices();
        }

        this.#reset();

        // Always clean up state variables.
        this.#state.dragging = false;
        this.#dragState = null;
        this.#originalParentList = null;
        this.#originalPosition = null;

        // Done dragging.
        this.classList.remove('dragging');
    }

    /**
     * Handles drag move event (mouse move or touch move).
     * @param {MouseEvent|TouchEvent} e The drag move event.
     * 
     * @memberof DraggableList
     */
    handleDragMove(e) {
        if (!this.#dragState || !this.#state.dragging) {
            return;
        }

        e.preventDefault();

        const isTouch = e.type === 'touchmove';
        const clientX = isTouch ? e.touches[0].clientX : e.clientX;
        const clientY = isTouch ? e.touches[0].clientY : e.clientY;

        // Update ghost element position for mouse events.
        if (this.#ghostElement && !isTouch) {
            cancelAnimationFrame(this.#animationFrameId);
            this.#animationFrameId = requestAnimationFrame(() => {
                const x = clientX - this.#dragState.offsetX;
                const y = clientY - this.#dragState.offsetY;

                this.#ghostElement.style.transform = `translate(${x}px, ${y}px)`;
            });
        }

        // Find element under pointer - use elementFromPoint with pointer events disabled temporarily to avoid the dragged element blocking detection.
        const draggedElement = this.#dragState.draggedNode.element;
        const originalPointerEvents = draggedElement.style.pointerEvents;

        draggedElement.style.pointerEvents = 'none';

        const elementFromPoint = document.elementFromPoint(clientX, clientY);

        // Restore original pointer events.
        draggedElement.style.pointerEvents = originalPointerEvents;

        if (!elementFromPoint) {
            return;
        }

        // Check if we're over a draggable-list.
        const currentHoverList = elementFromPoint.closest('draggable-list');

        if (!currentHoverList) {
            return;
        }

        const isCrossList = currentHoverList !== this.#dragState.parentList;

        // Store the current hover list for cleanup later.
        this.#dragState.currentHoverList = currentHoverList;

        // Check if we can drop on this list.
        const canDrop = isCrossList ? this.#canDropOn(currentHoverList) : true;

        if (canDrop) {
            if (isCrossList) {
                currentHoverList.classList.add('can-drop');
            }

            const targetElement = elementFromPoint ? elementFromPoint.closest('.draggable') : null;

            if (targetElement && targetElement !== draggedElement && targetElement.parentElement === currentHoverList) {
                const targetNode = currentHoverList.#nodes.get(targetElement);

                if (targetNode) {
                    this.#updateNodePosition(targetNode, clientX, clientY);
                }
            }
            else {
                // When not over a specific element, find the best position based on coordinates.
                this.#findClosestInsertPosition(currentHoverList, draggedElement, clientX, clientY);
            }
        }
        else if (currentHoverList) {
            currentHoverList.classList.remove('can-drop');
        }

        this.debouncedScroll(clientX, clientY);
    }

    /**
     * Handles drag start event (mouse down or touch start).
     * @param {MouseEvent|TouchEvent} e The drag start event.
     * 
     * @memberof DraggableList
     */
    handleDragStart(e) {
        const dragIndicator = e.target.closest('.drag-indicator');

        if (!dragIndicator) {
            return;
        }

        const draggableElement = dragIndicator.closest('.draggable');

        if (!draggableElement || draggableElement.parentElement !== this) {
            return;
        }

        e.preventDefault();

        const isTouch = e.type === 'touchstart';
        const clientX = isTouch ? e.touches[0].clientX : e.clientX;
        const clientY = isTouch ? e.touches[0].clientY : e.clientY;
        const rect = draggableElement.getBoundingClientRect();

        // Set initial state.
        this.#state.dragging = true;
        draggableElement.classList.add('dragging');

        // Store original position for potential restoration.
        this.#originalParentList = draggableElement.closest('draggable-list');

        // Store the original position of the element within its parent.
        const siblings = Array.from(this.#originalParentList.querySelectorAll(':scope > .draggable'));
        const originalIndex = siblings.indexOf(draggableElement);
        this.#originalPosition = {
            element: draggableElement,
            index: originalIndex,
            nextSibling: siblings[originalIndex + 1] || null,
            previousSibling: siblings[originalIndex - 1] || null
        };

        // Create ghost element for mouse events.
        if (!isTouch) {
            this.#ghostElement = draggableElement.cloneNode(true);
            this.#ghostElement.classList.add('ghost-element');

            Object.assign(this.#ghostElement.style, {
                className: 'ghost-element',
                height: `${draggableElement.offsetHeight}px`,
                left: '0',
                margin: '0',
                opacity: '0.9',
                pointerEvents: 'none',
                position: 'fixed',
                top: '0',
                transform: `translate(${clientX - (clientX - rect.left)}px, ${clientY - (clientY - rect.top)}px)`,
                width: `${draggableElement.offsetWidth}px`,
                zIndex: '1000'
            });

            // Append ghost to document body for cross-list dragging.
            document.body.appendChild(this.#ghostElement);
        }

        // Initialize drag state.
        const node = this.#nodes.get(draggableElement);
        if (node) {
            node.rect = rect;
            this.#dragState = {
                currentHoverList: null,
                draggedNode: node,
                initialIndex: node.index,
                lastPositionUpdate: 0,
                offsetX: isTouch ? 0 : clientX - rect.left,
                offsetY: isTouch ? 0 : clientY - rect.top,
                parentList: draggableElement.closest('draggable-list'),
                startX: clientX,
                startY: clientY
            };
        }
        else {
            // If we can't get the node, don't start dragging.
            this.#reset();

            return;
        }

        // Add appropriate event listeners.
        if (isTouch) {
            document.addEventListener('touchmove', this.handleDragMove, { passive: false });
            document.addEventListener('touchend', this.handleDragEnd);
            document.addEventListener('touchcancel', this.handleDragEnd);
        }
        else {
            document.addEventListener('mousemove', this.handleDragMove);
            document.addEventListener('mouseup', this.handleDragEnd);
        }

        // Indicate dragging.
        this.classList.add('dragging');
    };
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Adds event listeners for drag and touch events.
     * 
     * @memberof DraggableList
     * @private
     */
    #addEventListeners() {
        this.addEventListener('mousedown', this.handleDragStart);
        this.addEventListener('touchstart', this.handleDragStart, { passive: false });
    }

    /**
     * Automatically scrolls the page when the dragged item approaches the sides of the viewport.
     * @param {number} clientX The current X position.
     * @param {number} clientY The current Y position.
     * 
     * @memberof DraggableList
     * @private
     */
    #autoScroll(clientX, clientY) {
        // Viewport data.
        const { innerHeight, innerWidth } = this.ownerDocument.defaultView;

        if (clientY < this.#scrollThreshold) {
            // Scroll up.
            window.scrollBy(0, -this.#scrollSpeedVertical);
        }
        else if (clientY > innerHeight - this.#scrollThreshold) {
            // Scroll down.
            window.scrollBy(0, this.#scrollSpeedVertical);
        }

        if (clientX < this.#scrollThreshold) {
            // Scroll left.
            window.scrollBy(-this.#scrollSpeedHorizontal, 0);
        }
        else if (clientX > innerWidth - this.#scrollThreshold) {
            // Scroll right.
            window.scrollBy(this.#scrollSpeedHorizontal, 0);
        }

        // Schedule the next scroll check.
        this.#scrollTimeout = requestAnimationFrame(() => {
            return this.#autoScroll(clientX, clientY);
        });
    }

    /**
     * Checks if this list can drop items onto the target list.
     * @param {DraggableList} targetList The target list to check.
     * @returns {boolean} True if dropping is allowed, false otherwise.
     * 
     * @memberof DraggableList
     * @private
     */
    #canDropOn(targetList) {
        if (targetList) {
            // Allow if global cross-list is enabled.
            if (targetList.allowCrossList && this.#allowCrossList) {
                return true;
            }

            // Check if this list is allowed to drag to the target list.
            return this.#validDragSources.has(targetList.id);
        }

        return false;
    }

    /**
     * Dispatches item dropped event.
     * @param {string} id The dropped item ID.
     * @param {number} newIndex The new index position.
     * 
     * @memberof DraggableList
     * @private
     */
    #dispatchDropEvent(id, newIndex) {
        this.dispatchEvent(new CustomEvent('itemdropped', {
            detail: { id, newIndex }
        }));
    }

    /**
     * Finds the closest element to insert a dragged element near, based on cursor position.
     * @param {DraggableList} list The list to search within.
     * @param {HTMLElement} draggedElement The element being dragged.
     * @param {number} clientX Current X position of cursor.
     * @param {number} clientY Current Y position of cursor.
     * 
     * @memberof DraggableList
     * @private
     */
    #findClosestInsertPosition(list, draggedElement, clientX, clientY) {
        // Get all draggable elements in the list (excluding the dragged element).
        const draggableElements = [...list.querySelectorAll(':scope > .draggable')].filter((el) => {
            return el !== draggedElement;
        });

        if (draggableElements.length === 0) {
            // Empty list, just append.
            list.appendChild(draggedElement);
            list.#updateNodeIndices();

            return;
        }

        // Get the list's orientation.
        const isHorizontalList = list.classList.contains('horizontal-list');

        // Find the closest element by cursor position.
        let closestDistance = Number.MAX_VALUE,
            closestElement = null,
            insertBefore = false;

        for (const element of draggableElements) {
            const rect = element.getBoundingClientRect();

            // Calculate center point of the element.
            const centerX = rect.left + (rect.width / 2);
            const centerY = rect.top + (rect.height / 2);

            let distance;
            if (isHorizontalList) {
                distance = Math.abs(clientX - centerX);

                if (clientX < centerX && distance < closestDistance) {
                    // If we're to the left of the center, insert before.
                    closestDistance = distance;
                    closestElement = element;
                    insertBefore = true;
                }
                else if (clientX >= centerX && distance < closestDistance) {
                    // If we're to the right of the center, insert after.
                    closestDistance = distance;
                    closestElement = element;
                    insertBefore = false;
                }
            }
            else {
                distance = Math.abs(clientY - centerY);

                if (clientY < centerY && distance < closestDistance) {
                    // If we're above the center, insert before.
                    closestDistance = distance;
                    closestElement = element;
                    insertBefore = true;
                }
                else if (clientY >= centerY && distance < closestDistance) {
                    // If we're below the center, insert after.
                    closestDistance = distance;
                    closestElement = element;
                    insertBefore = false;
                }
            }
        }

        // Special case for top of the list.
        const firstElement = draggableElements[0];
        if (firstElement) {
            const firstRect = firstElement.getBoundingClientRect();

            if (isHorizontalList) {
                if (clientX < firstRect.left) {
                    closestElement = firstElement;
                    insertBefore = true;
                }
            }
            else if (clientY < firstRect.top) {
                closestElement = firstElement;
                insertBefore = true;
            }
        }

        // Special case for bottom of the list.
        const lastElement = draggableElements[draggableElements.length - 1];
        if (lastElement) {
            const lastRect = lastElement.getBoundingClientRect();

            if (isHorizontalList) {
                if (clientX > lastRect.right) {
                    closestElement = lastElement;
                    insertBefore = false;
                }
            }
            else if (clientY > lastRect.bottom) {
                closestElement = lastElement;
                insertBefore = false;
            }
        }

        // Perform the insertion.
        if (closestElement) {
            if (insertBefore) {
                closestElement.before(draggedElement);
            }
            else {
                closestElement.after(draggedElement);
            }
        }
        else {
            // Fallback - append to the end.
            list.appendChild(draggedElement);
        }

        // Always update node indices after insertion.
        list.#updateNodeIndices();
    }

    /**
     * Initializes cross-list drag and drop settings from attributes.
     * 
     * @memberof DraggableList
     * @private
     */
    #initializeCrossListSettings() {
        // Check for global cross-list setting.
        this.#allowCrossList = this.hasAttribute('allow-cross-list');

        // Parse drop targets.
        this.#validDropTargets.clear();
        const dropTargetAttr = this.getAttribute('drop-target');
        if (dropTargetAttr) {
            dropTargetAttr.split(',').forEach((id) => {
                this.#validDropTargets.add(id.trim());
            });
        }

        // Parse drag sources.
        this.#validDragSources.clear();
        const dragSourceAttr = this.getAttribute('drag-source');
        if (dragSourceAttr) {
            dragSourceAttr.split(',').forEach((id) => {
                this.#validDragSources.add(id.trim());
            });
        }
    }

    /**
     * Initializes draggable nodes.
     * 
     * @memberof DraggableList
     * @private
     */
    #initializeNodes() {
        // Clear existing nodes.
        this.#nodes.clear();

        // Get all direct draggable children.
        [...this.querySelectorAll(':scope > .draggable')].forEach((element, index) => {
            this.#nodes.set(element, {
                element,
                index,
                parentList: this,
                rect: element.getBoundingClientRect()
            });
        });
    }

    /**
     * Removes event listeners.
     * 
     * @memberof DraggableList
     * @private
     */
    #removeEventListeners() {
        this.removeEventListener('mousedown', this.handleDragStart);
        this.removeEventListener('touchstart', this.handleDragStart);

        // Clean up any ongoing drag operation.
        if (this.#state.dragging) {
            document.removeEventListener('mousemove', this.handleDragMove);
            document.removeEventListener('mouseup', this.handleDragEnd);
            document.removeEventListener('touchmove', this.handleDragMove);
            document.removeEventListener('touchend', this.handleDragEnd);
            document.removeEventListener('touchcancel', this.handleDragEnd);
        }
    }

    /**
     * Resets drag/drop/scroll operation, timeouts and intervals.
     * 
     * @memberof DraggableList
     * @private
     */
    #reset() {
        // Clean up ghost element.
        if (this.#ghostElement) {
            this.#ghostElement.remove();
            this.#ghostElement = null;
        }

        // Cancel animation frame.
        if (this.#animationFrameId) {
            cancelAnimationFrame(this.#animationFrameId);
            this.#animationFrameId = null;
        }

        // Clear timeouts.
        if (this.#touchTimeout) {
            clearTimeout(this.#touchTimeout);
            this.#touchTimeout = null;
        }

        if (this.#scrollTimeout) {
            window.cancelAnimationFrame(this.#scrollTimeout);
            this.#scrollTimeout = null;
        }
    }

    /**
     * Restores the element to its original position in the list.
     * @param {HTMLElement} element The element to restore.
     * 
     * @memberof DraggableList
     * @private
     */
    #restoreOriginalPosition(element) {
        if (!this.#originalParentList || !this.#originalPosition) {
            return;
        }

        // First, remove the element from its current parent if it has one.
        if (element.parentElement) {
            element.parentElement.removeChild(element);
        }

        // Then reinsert at the original position.
        if (this.#originalPosition.nextSibling) {
            this.#originalParentList.insertBefore(element, this.#originalPosition.nextSibling);
        }
        else if (this.#originalPosition.previousSibling) {
            // Insert after the previous sibling.
            const prev = this.#originalPosition.previousSibling;
            if (prev.nextSibling) {
                this.#originalParentList.insertBefore(element, prev.nextSibling);
            }
            else {
                this.#originalParentList.appendChild(element);
            }
        }
        else {
            // If no siblings, it was the only element.
            this.#originalParentList.appendChild(element);
        }
    }

    /**
     * Updates node indices after position change.
     * 
     * @memberof DraggableList
     * @private
     */
    #updateNodeIndices() {
        // Clear and re-initialize nodes.
        this.#nodes.clear();

        [...this.querySelectorAll(':scope > .draggable')].forEach((element, index) => {
            this.#nodes.set(element, {
                element,
                index,
                parentList: this,
                rect: element.getBoundingClientRect()
            });
        });

        this.#state.nodes = [...this.#nodes.values()];
    }

    /**
     * Updates node position during drag, for both same-list and cross-list operations.
     * @param {Object} targetNode The target node.
     * @param {number} clientX Current X position of mouse/touch.
     * @param {number} clientY Current Y position of mouse/touch.
     * 
     * @memberof DraggableList
     * @private
     */
    #updateNodePosition(targetNode, clientX, clientY) {
        const draggedElement = this.#dragState.draggedNode.element;
        const targetElement = targetNode.element;

        // Skip if attempting to position relative to itself.
        if (targetElement === draggedElement) {
            return;
        }

        // Get the target element's dimensions.
        const targetRect = targetNode.rect || targetElement.getBoundingClientRect();

        // Apply minimal throttling to prevent excessive DOM operations.
        const now = Date.now();
        const throttle = 35;

        if (this.#dragState.lastPositionUpdate && (now - this.#dragState.lastPositionUpdate) < throttle) {
            return;
        }

        // Determine if the list is horizontal by checking for a class or computing style.
        const listElement = targetElement.closest('draggable-list');
        const isHorizontalList = listElement.classList.contains('horizontal-list');

        // Determine position based on cursor location relative to target center.
        let positionBefore = false;

        if (isHorizontalList) {
            positionBefore = clientX < (targetRect.left + (targetRect.width / 2));
        }
        else {
            positionBefore = clientY < (targetRect.top + (targetRect.height / 2));
        }

        // Only update DOM if the position needs to change.
        let shouldReposition = false;

        if (positionBefore) {
            shouldReposition = targetElement.previousElementSibling !== draggedElement;
        }
        else {
            shouldReposition = targetElement.nextElementSibling !== draggedElement;
        }

        if (shouldReposition) {
            // Store timestamp of this update for throttling.
            this.#dragState.lastPositionUpdate = now;

            if (positionBefore) {
                targetElement.before(draggedElement);
            }
            else {
                targetElement.after(draggedElement);
            }

            // Update the dragged node's parent list if it has changed.
            if (this.#dragState.parentList !== listElement) {
                this.#dragState.parentList = listElement;
            }

            // Update positions after DOM change - only update the current list.
            this.#updateNodeIndices();
        }
    }
    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/

/**
 * Register web component.
 * Defines the custom element 'draggable-list' and maps it to the DraggableList class.
 */
customElements.define(DraggableList.tagName, DraggableList);
